/** Generation jobs started from the prompt bar, listed in "Recent creations" (kept for the browser session). */
import { useCallback, useEffect, useState } from 'react';
import { audioStudioApi, type GenerateBody, type LookalikeBody, type StudioJob } from '@/api/audioStudioApi';

/** What was sent to start the job — kept so a failed job can be retried from its row. */
export type StudioJobRequest =
  | { kind: 'generate'; body: GenerateBody }
  | { kind: 'lookalike'; trendId: string; body: LookalikeBody; where: { region?: string; period?: number } };

export interface StudioJobRef {
  jobId: string;
  /** What was asked for, e.g. "Inspired by “Song”" or the start of the prompt. */
  label?: string;
  /** The trend the sound is modelled on (lookalikes) — the row reads "Like “<title>”". */
  trendTitle?: string;
  /** Requested length in seconds. */
  durationS?: number;
  request?: StudioJobRequest;
}

export const JOBS_KEY = 'audioStudio.createJobs';
const MAX_JOBS = 6;

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

function readRequest(v: unknown): StudioJobRequest | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const r = v as Partial<StudioJobRequest> & { trendId?: unknown; body?: unknown; where?: unknown };
  if (!r.body || typeof r.body !== 'object') return undefined;
  if (r.kind === 'generate') return { kind: 'generate', body: r.body as GenerateBody };
  if (r.kind === 'lookalike' && str(r.trendId)) {
    return { kind: 'lookalike', trendId: r.trendId as string, body: r.body as LookalikeBody, where: (r.where && typeof r.where === 'object' ? r.where : {}) as { region?: string; period?: number } };
  }
  return undefined;
}

/** Tolerates the older format (a plain list of job ids) and junk. */
export function readStoredJobs(): StudioJobRef[] {
  try {
    const raw = JSON.parse(sessionStorage.getItem(JOBS_KEY) || '[]') as unknown;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((x): StudioJobRef | null => {
        if (typeof x === 'string' && x) return { jobId: x };
        if (x && typeof x === 'object' && typeof (x as StudioJobRef).jobId === 'string') {
          const o = x as Record<string, unknown>;
          const ref: StudioJobRef = { jobId: o.jobId as string, label: str(o.label) };
          if (str(o.trendTitle)) ref.trendTitle = o.trendTitle as string;
          if (typeof o.durationS === 'number' && Number.isFinite(o.durationS)) ref.durationS = o.durationS;
          const request = readRequest(o.request);
          if (request) ref.request = request;
          return ref;
        }
        return null;
      })
      .filter((x): x is StudioJobRef => x !== null)
      .slice(0, MAX_JOBS);
  } catch {
    return [];
  }
}

/** Starts (or restarts) a generation from a stored request. */
export function startJobRequest(r: StudioJobRequest): Promise<StudioJob> {
  return r.kind === 'generate'
    ? audioStudioApi.generate(r.body)
    : audioStudioApi.trendLookalike(r.trendId, r.body, r.where);
}

export function useStudioJobs() {
  const [jobs, setJobs] = useState<StudioJobRef[]>(readStoredJobs);
  useEffect(() => {
    try { sessionStorage.setItem(JOBS_KEY, JSON.stringify(jobs.slice(0, MAX_JOBS))); } catch { /* storage unavailable */ }
  }, [jobs]);
  const addJob = useCallback((job: StudioJobRef) => setJobs((j) => [job, ...j.filter((x) => x.jobId !== job.jobId)].slice(0, MAX_JOBS)), []);
  const dismissJob = useCallback((jobId: string) => setJobs((j) => j.filter((x) => x.jobId !== jobId)), []);
  return { jobs, addJob, dismissJob };
}
